import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useGlobalStore } from "@/GlobalStore";
import { cn } from "@/lib/utils";
import { extendAccountWithIcons } from "@/screens/utils/extendInsightAccount";
import { LuLoaderCircle } from "react-icons/lu";
import { useState } from "react";
import AdsPlatformsImage from "@/assets/adsPlatform.svg";
import useAccountsList from "@/hooks/useAccountsList";
import { mergeAccount } from "@/api/globalApiService";
import { SqButton } from "@/components/squircle-ui/button";
import { RiGitForkLine } from "@remixicon/react";
import { Separator } from "@/components/ui/separator";
import ToastCompact from "@/components/common/toast/ToastCompact";
import { toast } from "sonner";

interface CombineAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CombineAccountDialog = ({ open, onOpenChange }: CombineAccountDialogProps) => {
  const { noAccountsAvailable, setAccountList, accountList } = useGlobalStore();
  const {
    isLoading,
    effectiveAccountList,
    onScrollNextPageLoader,
    listContainerRefCallback,
    searchQuery,
    setSearchQuery,
    handleAccountChange,
  } = useAccountsList();

  // State to track selected account IDs
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(new Set());

  // State to manage dialog flow
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [combinedAccountName, setCombinedAccountName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Handle checkbox toggle
  const handleCheckboxChange = (accountId: string, checked: boolean) => {
    setSelectedAccountIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(accountId);
      } else {
        newSet.delete(accountId);
      }
      return newSet;
    });
  };

  // Handle creating combined account
  const handleCreateCombinedAccount = async () => {
    if (!combinedAccountName.trim()) return;
    setIsCreating(true);
    try {
      const res = await mergeAccount({
        account_ids: Array.from(selectedAccountIds),
        account_name: combinedAccountName,
      });
      setAccountList([...accountList, res]);
      // Reset states and close both dialogs
      setSelectedAccountIds(new Set());
      setCombinedAccountName("");
      setShowNameDialog(false);
      onOpenChange(false);
      toast.custom(
        (t) => (
          <ToastCompact
            messagePrefix={`${combinedAccountName} created successfully`}
            iconProps={{ size: 16 }}
            onDismiss={() => toast.dismiss(t)}
          />
        ),
        {
          duration: 3000,
        },
      );
      // change account
      handleAccountChange(res, true);
    } catch (error) {
      console.error("Error creating combined account:", error);
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  // Handle going back to account selection
  const handleBackToSelection = () => {
    setShowNameDialog(false);
    setCombinedAccountName("");
    setSelectedAccountIds(new Set());
  };

  const normalAccounts = effectiveAccountList.filter((a) => a.type !== "MERGED");

  return (
    <>
      {/* Account Selection Dialog */}
      <Dialog open={open && !showNameDialog} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-[464px]  flex flex-col items-start min-h-[600px] justify-between radius-[12px] p-0 px-1 gap-0 pt-4"
          hideCloseButton
        >
          <div className="w-full">
            <div className="flex items-center justify-between mx-3 mb-1">
              <div className="font-medium text-[14px] leading-[19.35px] text-[#021859]">
                Select accounts to combine
              </div>
            </div>
            <Command
              className="relative h-[520px]"
              filter={(value, search) => {
                // Since value is now the label, we can use it directly
                const decodedValue = decodeURIComponent(value);
                return decodedValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
              }}
              shouldFilter={false}
            >
              <CommandInput
                wrapperClass="border rounded-[8px] mt-2 mx-3 h-[30px]"
                className="py-1 h-8 text-xs text-brand-primary-new placeholder:text-[#A0ABBC] placeholder:text-xs placeholder:font-normal"
                value={searchQuery}
                placeholder="Search"
                onValueChange={setSearchQuery}
                disabled={noAccountsAvailable}
                iconClass="hidden"
              />

              {normalAccounts.length > 0 && (
                <CommandGroup
                  ref={listContainerRefCallback}
                  onScroll={onScrollNextPageLoader}
                  className="overflow-y-auto mt-2 px-0 mx-0"
                >
                  {normalAccounts.map(extendAccountWithIcons).map((item, index) => (
                    <CommandItem
                      key={`${item.id}-${index}`}
                      value={encodeURIComponent(item.name)}
                      onSelect={() => {
                        // Toggle checkbox when item is clicked
                        const isSelected = selectedAccountIds.has(item.id);
                        handleCheckboxChange(item.id, !isSelected);
                      }}
                      className={cn(
                        "cursor-pointer rounded-none justify-between gap-2 h-auto px-5 items-start text-brand-primary-new",
                        "hover:bg-[#F6F7F8]",
                      )}
                    >
                      <div className="flex items-start flex-row justify-between w-full">
                        <div className="flex items-start">
                          <Checkbox
                            checked={selectedAccountIds.has(item.id)}
                            onCheckedChange={(checked) => {
                              handleCheckboxChange(item.id, checked as boolean);
                            }}
                            className="mr-3 mt-0.5"
                          />
                          {item?.labelPrefixIcon &&
                            (typeof item.labelPrefixIcon === "string" ? (
                              <img
                                src={item.labelPrefixIcon}
                                alt=""
                                className="w-4 h-4 mr-2 shrink-0 mt-0.5 object-contain"
                              />
                            ) : (
                              <item.labelPrefixIcon
                                size={18}
                                color="#FF4500"
                                className="mr-2 shrink-0 mt-0.5"
                              />
                            ))}
                          <div>
                            <span
                              style={{
                                overflowWrap: "anywhere",
                              }}
                              className={cn(
                                "hyphens-auto font-inter text-xs leading-[19.2px] tracking-[0.02em] text-left text-brand-primary-new",
                              )}
                            >
                              {item.name}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">{item.statusIcon}</div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {noAccountsAvailable ? (
                <div className="w-full h-full flex flex-col items-center justify-center p-2 my-16">
                  <img src={AdsPlatformsImage} />
                  <div className="font-medium text-[14px] leading-[100%] tracking-[-1%] text-center text-brand-primary-new mt-8">
                    Start by connecting your Ad account
                  </div>
                  <p className="font-normal text-[12px] leading-[100%] tracking-[0%] text-center text-[#617390] mt-2">
                    Click the button below to connect your <br /> account to the platform
                  </p>
                </div>
              ) : (
                !isLoading &&
                normalAccounts.length === 0 && (
                  <div className="py-6 flex justify-center text-sm text-gray-500">
                    No results found
                  </div>
                )
              )}

              {isLoading && (
                <div className="h-12 flex justify-center items-center">
                  <LuLoaderCircle className="animate-spin" color="#FF4500" size={18} />
                </div>
              )}
            </Command>
          </div>

          {/* Footer buttons */}
          <div className="flex justify-end gap-3 px-3 py-4 pt-2 w-full">
            <SqButton
              variant="outline"
              onClick={() => {
                setSelectedAccountIds(new Set());
                setCombinedAccountName("");
                setShowNameDialog(false);
                onOpenChange(false);
              }}
              size={"md"}
              shadow={"sm"}
              className="font-medium text-[12px] leading-[19.16px] tracking-[0.02em] text-[#021859]"
            >
              Cancel
            </SqButton>
            <SqButton
              variant="fill"
              disabled={selectedAccountIds.size === 0}
              onClick={() => {
                setShowNameDialog(true);
              }}
              size={"md"}
              shadow={"sm"}
              className="font-normal not-italic text-[11px] leading-[19.16px] tracking-[0.02em] text-white font-['Helvetica_Neue','Helvetica','Arial','sans-serif']"
            >
              <RiGitForkLine size={14} />
              <span className="font-normal not-italic text-[11px] leading-[19.16px] tracking-[0.02em] text-white font-['Helvetica_Neue','Helvetica','Arial','sans-serif']">
                Create combined account
              </span>
            </SqButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* Name Combined Account Dialog */}
      <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
        <DialogContent
          className="sm:max-w-[780px] flex flex-col items-start justify-between radius-[12px] p-0 gap-0 pt-5 rounded-[16px] border-[1px] border-border-gray"
          hideCloseButton
        >
          <div className="w-full px-6">
            <div className="mb-4">
              <h2 className="text-[15px] font-medium text-[#021859] mb-2 leading-[18px] tracking-[0.02em]">
                Name your combined account
              </h2>
              <p className="font-normal not-italic text-[14px] leading-[18px] tracking-[0.02em] text-[#02185999]">
                Set a title for the combined view, we'll use it to reference these
                combined accounts.
              </p>
            </div>
            <Input
              value={combinedAccountName}
              onChange={(e) => setCombinedAccountName(e.target.value)}
              placeholder="Combined Account Title"
              className="w-full h-[32px] px-3 text-[11px] focus:outline-none placeholder:text-brand-primary-new/40 rounded-[8px] mb-4 shadow-none text-brand-primary-new"
            />
          </div>
          <Separator className="mb-1" />

          {/* Footer buttons */}
          <div className="flex justify-end gap-3 px-3 py-4 pt-2 w-full">
            <SqButton
              variant="outline"
              onClick={handleBackToSelection}
              className="font-medium text-[12px] leading-[19.16px] tracking-[0.02em] text-[#021859]"
              size={"md"}
              shadow={"sm"}
            >
              Cancel
            </SqButton>
            <SqButton
              variant="fill"
              disabled={!combinedAccountName.trim() || isCreating}
              onClick={() => {
                void handleCreateCombinedAccount();
              }}
              className="font-medium text-[12px] leading-[19.16px] tracking-[0.02em] text-white"
              size={"md"}
              shadow={"sm"}
            >
              Create
            </SqButton>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CombineAccountDialog;
